"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useMemo } from "react";
import { ArrowRight, BookOpen, Calendar, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
const articlesCopy = {
  badge: {
    en: "Technical Resources",
    zh: "技术资源",
    es: "Recursos técnicos",
    de: "Technische Ressourcen",
    fr: "Ressources techniques",
    pt: "Recursos técnicos"
  },
  heading: {
    en: "Related technical articles",
    zh: "相关技术文章",
    es: "Artículos técnicos relacionados",
    de: "Verwandte Fachartikel",
    fr: "Articles techniques associés",
    pt: "Artigos técnicos relacionados"
  },
  description: {
    en: "Browse engineering guides, alloy performance analyses, and sintered mesh selection standards.",
    zh: "浏览工程技术指南、合金性能分析及烧结网选型标准。",
    es: "Explore guías de ingeniería, análisis de rendimiento de aleaciones y normas de selección de mallas sinterizadas.",
    de: "Entdecken Sie technische Leitfäden, Legierungsanalysen und Auswahlstandards für Sintergewebe.",
    fr: "Consultez nos guides techniques, analyses de performances des alliages et normes de sélection des toiles frittées.",
    pt: "Explore guias de engenharia, análises de ligas metálicas e padrões de especificação de malhas sinterizadas."
  },
  viewAll: {
    en: "View all resources",
    zh: "查看全部资源",
    es: "Ver todos los recursos",
    de: "Alle Ressourcen ansehen",
    fr: "Voir toutes les ressources",
    pt: "Ver todos os recursos"
  },
  errorMsg: {
    en: "Unable to load technical articles right now.",
    zh: "当前无法加载技术文章。",
    es: "No se pueden cargar los artículos técnicos en este momento.",
    de: "Fachartikel können derzeit nicht geladen werden.",
    fr: "Impossible de charger les articles techniques pour le moment.",
    pt: "Não foi possível carregar os artigos técnicos no momento."
  },
  retry: {
    en: "Retry",
    zh: "重试",
    es: "Reintentar",
    de: "Erneut versuchen",
    fr: "Réessayer",
    pt: "Tentar novamente"
  },
  emptyTitle: {
    en: "No articles found",
    zh: "暂无相关文章",
    es: "No se encontraron artículos",
    de: "Keine Artikel gefunden",
    fr: "Aucun article trouvé",
    pt: "Nenhum artigo encontrado"
  },
  emptyDesc: {
    en: "Check back soon for updated sintered mesh guides and technical publications.",
    zh: "请稍后查看更新的烧结网选型指南与技术文献。",
    es: "Vuelva pronto para consultar nuevas publicaciones técnicas y guías de mallas sinterizadas.",
    de: "Schauen Sie bald wieder vorbei für aktualisierte Leitfäden und Fachpublikationen zu Sintergeweben.",
    fr: "Revenez prochainement pour découvrir de nouveaux guides et publications techniques.",
    pt: "Volte em breve para conferir novos guias e publicações técnicas sobre malha sinterizada."
  },
  defaultCategory: {
    en: "Technical Guide",
    zh: "技术指南",
    es: "Guía técnica",
    de: "Technischer Leitfaden",
    fr: "Guide technique",
    pt: "Guia técnico"
  },
  published: {
    en: "Published",
    zh: "已发布",
    es: "Publicado",
    de: "Veröffentlicht",
    fr: "Publié",
    pt: "Publicado"
  },
  readTime: {
    en: "4 min read",
    zh: "4 分钟阅读",
    es: "4 min de lectura",
    de: "4 Min. Lesezeit",
    fr: "Lecture 4 min",
    pt: "4 min de leitura"
  },
  readArticle: {
    en: "Read article",
    zh: "阅读全文",
    es: "Leer artículo",
    de: "Artikel lesen",
    fr: "Lire l'article",
    pt: "Ler artigo"
  }
};
function ArticlesConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-lg border border-border bg-card">
            <div className="h-48 w-full bg-muted" />
            <div className="flex flex-1 flex-col p-6 space-y-4">
              <div className="h-4 w-24 rounded bg-muted" />
              <div className="h-6 w-3/4 rounded bg-muted" />
              <div className="h-4 w-full rounded bg-muted" />
              <div className="h-4 w-5/6 rounded bg-muted" />
              <div className="mt-auto pt-4 flex items-center justify-between border-t border-border">
                <div className="h-4 w-20 rounded bg-muted" />
                <div className="h-4 w-16 rounded bg-muted" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {pick(lang, articlesCopy.errorMsg)}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          {pick(lang, articlesCopy.retry)}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-12 text-center">
        <BookOpen className="mx-auto size-10 text-muted-foreground" />
        <h3 className="mt-4 text-base font-semibold text-foreground">
          {pick(lang, articlesCopy.emptyTitle)}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {pick(lang, articlesCopy.emptyDesc)}
        </p>
      </div>;
  }
  const localeMap: Record<Lang, string> = {
    en: "en-US",
    zh: "zh-CN",
    es: "es-ES",
    de: "de-DE",
    fr: "fr-FR",
    pt: "pt-PT"
  };
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : pick(lang, articlesCopy.defaultCategory);
      const formattedDate = post.date ? new Date(post.date).toLocaleDateString(localeMap[lang] || "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      }) : pick(lang, articlesCopy.published);
      return <article key={post.id} data-index={i} className="group flex flex-col h-full overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary/50">
            {post.featuredImage ? <div className="mf-card-media mf-ratio-card w-full">
                <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div> : <div className="mf-card-media mf-ratio-card w-full flex items-center justify-center text-muted-foreground bg-muted">
                <BookOpen className="size-10 stroke-[1.5]" aria-hidden="true" />
              </div>}

            <div className="flex flex-1 flex-col p-6">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="text-[13px] font-normal bg-muted text-foreground">
                  {categoryName}
                </Badge>
                <div className="flex items-center gap-1 text-[13px] text-muted-foreground ml-auto">
                  <SafeHydrate><Calendar className="size-3.5" aria-hidden="true" /></SafeHydrate>
                  <span>{formattedDate}</span>
                </div>
              </div>

              <h3 className="mf-h3 font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </h3>

              {post.excerpt && <div className="mt-2.5 text-[13px] text-muted-foreground line-clamp-3 leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}

              <div className="mt-auto pt-5 border-t border-border flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                  <Clock className="size-3.5" aria-hidden="true" />
                  <span>{pick(lang, articlesCopy.readTime)}</span>
                </div>

                <Link to={post.link || "#"} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                  <span>{pick(lang, articlesCopy.readArticle)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  const wp_query = useMemo(() => {
    const params = {
      post_type: "post",
      per_page: 6,
      order: "desc",
      orderby: "date"
    };
    return new WP_Query(params);
  }, []);
  return <section id="relatedarticles" data-nav="light" className="bg-background mf-section border-t border-border">
      <div className="mf-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted border border-border text-[13px] font-medium text-foreground mb-3">
              {pick(lang, articlesCopy.badge)}
            </div>
            <h2 className="mf-h2 font-bold tracking-tight text-foreground font-default text-balance">
              {pick(lang, articlesCopy.heading)}
            </h2>
            <p className="mt-2 text-base text-muted-foreground max-w-2xl font-default">
              {pick(lang, articlesCopy.description)}
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline">
              <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-2">
                <span>{pick(lang, articlesCopy.viewAll)}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={wp_query} className="w-full">
          <ArticlesConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}